import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Link2, UserRound, X } from "lucide-react";
import { poemsLinkChild, poemsLinkedChildren, poemsUnlinkChild, errorMessage } from "./api";
import { card, field, Modal } from "./ui";

// "Link your child": a parent types the code their child's school gave them and the child's progress appears in their dashboard.
// The (!) button next to it opens a short explanation.
const why = (e) => { const d = e?.response?.data?.detail; return typeof d === "object" && d && d.message ? d.message : errorMessage(e, "Couldn't link that code"); };

export default function LinkChild({ token, onChanged, goOverview }) {
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [kids, setKids] = useState([]);
  const [unlink, setUnlink] = useState(null);
  const load = useCallback(() => poemsLinkedChildren(token).then((l) => setKids((l || []).filter((x) => x.my_role === "linked"))).catch(() => setKids([])), [token]);
  useEffect(() => { load(); }, [load]);

  const link = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await poemsLinkChild(token, code.trim());
      toast.success(r.already_linked ? `${r.display_name} is already linked` : `${r.display_name} is now linked`);
      setOpen(false); setCode(""); load(); if (onChanged) onChanged();
    } catch (err) { toast.error(why(err)); } finally { setBusy(false); }
  };
  const doUnlink = async () => {
    setBusy(true);
    try { await poemsUnlinkChild(token, unlink.learner_id); toast.success(`${unlink.display_name} was unlinked`); setUnlink(null); load(); if (onChanged) onChanged(); }
    catch (err) { toast.error(why(err)); } finally { setBusy(false); }
  };

  return (
    <section className="space-y-3" data-testid="link-child">
      <div className={`${card} relative flex flex-wrap items-center gap-3`}>
        <span className="h-10 w-10 rounded-full bg-forest-600/10 text-forest-700 grid place-items-center shrink-0"><Link2 className="h-5 w-5" /></span>
        <div className="flex-1 min-w-[200px]"><div className="font-semibold text-forest-700">Link your child</div>
          <div className="text-sm text-foreground/70">Use the code your child&apos;s school gave you to see their progress here.</div></div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" className="btn-primary" data-testid="link-child-open" onClick={() => setOpen(true)}>Link your child</button>
          <button type="button" onClick={() => setTip(!tip)} aria-label="About linking your child" aria-expanded={tip} data-testid="link-child-info"
            className="h-9 w-9 rounded-full border border-sand-300 bg-white text-forest-700 inline-flex items-center justify-center hover:bg-sand-100"><AlertCircle className="h-5 w-5" /></button>
        </div>
        {tip && (
          <div role="status" data-testid="link-child-tip" className="absolute right-3 top-full mt-2 z-30 w-80 max-w-[85vw] rounded-2xl border border-sand-300 bg-white p-4 text-sm shadow-lg">
            <span aria-hidden="true" className="absolute -top-2 right-5 h-4 w-4 rotate-45 border-l border-t border-sand-300 bg-white" />
            <button type="button" onClick={() => setTip(false)} aria-label="Close" className="absolute right-2 top-2 rounded-full p-1 text-foreground/60 hover:bg-sand-100 hover:text-forest-700"><X className="h-4 w-4" /></button>
            <div className="pe-6 font-semibold text-forest-700">What does linking do?</div>
            <ul className="mt-2 list-disc space-y-1 ps-5 text-foreground/80">
              <li>Your child&apos;s school gives you a short link code.</li>
              <li>Enter it here and your child&apos;s progress from school shows up in your dashboard.</li>
              <li>You can follow their progress from home. Their teacher manages the class and the profile.</li>
              <li>You can unlink at any time. The child&apos;s record stays with the school.</li>
            </ul>
          </div>)}
      </div>
      {kids.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="linked-children">
          {kids.map((k) => (
            <div key={k.learner_id} className={`${card} flex items-center gap-3`} data-testid="linked-child">
              <span className="h-10 w-10 rounded-full bg-forest-600/10 text-forest-700 grid place-items-center"><UserRound className="h-5 w-5" /></span>
              <div className="flex-1 min-w-0"><div className="font-semibold text-forest-700 truncate">{k.display_name}</div><div className="text-xs text-foreground/60">Linked from school</div></div>
              {goOverview && <button type="button" className="btn-primary text-xs !px-3 !py-1.5" onClick={goOverview} data-testid={`view-progress-${k.learner_id}`}>View progress</button>}
              <button type="button" className="btn-ghost text-xs !px-2.5 !py-1.5" onClick={() => setUnlink(k)} data-testid={`unlink-${k.learner_id}`}>Unlink</button>
            </div>))}
        </div>)}
      {open && (
        <Modal title="Link your child" onClose={() => setOpen(false)}>
          <form onSubmit={link} className="space-y-4" data-testid="link-child-form">
            <label className="block text-sm font-semibold text-forest-700">Link code
              <input required autoFocus className={`${field} mt-1 font-mono tracking-widest uppercase`} placeholder="e.g. K7M2QX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={16} data-testid="link-code-input" /></label>
            <p className="text-xs text-foreground/60">Ask your child&apos;s class teacher or school office if you don&apos;t have the code yet.</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary disabled:opacity-50" disabled={busy || !code.trim()} data-testid="link-child-submit">{busy ? "Linking…" : "Link child"}</button>
            </div>
          </form>
        </Modal>)}
      {unlink && (
        <Modal title="Unlink child" onClose={() => setUnlink(null)}>
          <div className="space-y-4">
            <p className="text-sm text-foreground/80">Stop following <strong>{unlink.display_name}</strong> from your account? Their progress stays with the school and you can link again with the code.</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setUnlink(null)}>Cancel</button>
              <button type="button" className="btn-primary disabled:opacity-50" disabled={busy} onClick={doUnlink} data-testid="unlink-confirm">{busy ? "Please wait…" : "Unlink"}</button>
            </div>
          </div>
        </Modal>)}
    </section>
  );
}
